<template>
  <div class="my-5 flex items-center justify-between gap-4">
    <div class="text-xl font-bold dark:text-gray-100">用户总数: {{ totalUsers }}</div>
    <button
      v-if="authStore.isSuperAdmin"
      type="button"
      class="primary-btn"
      @click="openCreateUserModal"
    >
      创建用户
    </button>
  </div>

  <!-- Search and Filter Section -->
  <div class="flex justify-between items-center mb-4 gap-4">
    <div class="flex-1">
      <input type="text" v-model="searchQuery" placeholder="搜索用户名、邮箱或手机号"
        class="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        @input="handleSearch" />
    </div>
    <div class="w-40">
      <select v-model="statusFilter"
        class="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        @change="handleSearch">
        <option value="all">全部状态</option>
        <option value="active">有效会员</option>
        <option value="expired">已过期</option>
      </select>
    </div>
  </div>

  <div class="">
    <div class="h-[calc(100vh-260px)] overflow-y-auto relative">
      <div class="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
        <div class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
          <!-- Main Users Table -->
          <div class="relative">
            <!-- Sticky Header -->
            <div class="sticky top-0 bg-white dark:bg-gray-800 px-2 z-20">
              <div class="flex min-w-full">
                <div class="w-[6%] py-3.5 pr-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100 sm:pl-1">
                  ID
                </div>
                <div class="w-[12%] px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">
                  用户名
                </div>
                <div class="w-[13%] px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">
                  联系方式
                </div>
                <div class="w-[9%] px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">
                  角色
                </div>
                <div class="w-[12%] px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">
                  所属租户
                </div>
                <div class="w-[13%] px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">
                  当前套餐
                </div>
                <div class="w-[10%] px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">
                  订阅状态
                </div>
                <div class="w-[15%] px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">
                  订阅到期时间
                </div>
                <div class="w-[10%] px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">
                  <span class="sr-only">操作</span>
                </div>
              </div>
            </div>

            <!-- Users List -->
            <div class="divide-y divide-gray-200 dark:divide-gray-700 px-2">
              <div v-for="user in users" :key="user.id" class="flex items-center min-w-full hover:bg-indigo-50 dark:hover:bg-gray-700">
                <div class="w-[6%] py-4 pr-3 text-sm font-medium text-gray-900 dark:text-gray-100 sm:pl-1">
                  {{ user.id }}
                </div>
                <div class="w-[12%] px-3 py-4 text-md text-gray-600 dark:text-gray-300">
                  {{ user.username }}
                </div>
                <div class="w-[13%] px-3 py-4 text-md text-gray-600 dark:text-gray-300">
                  {{ user.phone || user.email || '-' }}
                </div>
                <div class="w-[9%] px-3 py-4 text-md">
                  <span :class="getRoleBadgeClass(user.role)">
                    {{ roleMap[user.role] }}
                  </span>
                </div>
                <!-- 所属租户 -->
                <div class="w-[12%] px-3 py-4 text-sm text-gray-600 dark:text-gray-300">
                  <span v-if="user.tenantBinding">
                    {{ user.tenantBinding.tenantName }}
                  </span>
                  <span v-else class="text-gray-400 dark:text-gray-500">
                    未绑定
                  </span>
                </div>
                <!-- 当前套餐 -->
                <div class="w-[13%] px-3 py-4 text-md text-gray-600 dark:text-gray-300">
                  <span v-if="user.activePlans && user.activePlans.length > 0" class="font-medium text-indigo-600 dark:text-indigo-400">
                    {{user.activePlans.map(p => p.name).join(', ')}}
                  </span>
                  <span v-else class="text-gray-400 dark:text-gray-500">
                    无套餐
                  </span>
                </div>
                <!-- 订阅状态 -->
                <div class="w-[10%] px-3 py-4 text-md">
                  <span v-if="user.activeSubscription"
                    :class="getSubscriptionStatusClass(user.activeSubscription.status)">
                    {{ formatSubscriptionStatus(user.activeSubscription.status) }}
                  </span>
                  <span v-else
                    class="inline-flex items-center rounded-md bg-gray-100 dark:bg-gray-700 px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-400">
                    无订阅
                  </span>
                </div>
                <!-- 订阅到期时间 -->
                <div class="w-[15%] px-3 py-4 text-md text-gray-600 dark:text-gray-300">
                  <span v-if="user.activeSubscription"
                    :class="[isSubscriptionActive(user.activeSubscription) ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400']">
                    {{ formatDate(user.activeSubscription.endDate) }}
                  </span>
                  <span v-else class="text-gray-400 dark:text-gray-500">
                    -
                  </span>
                </div>
                <div class="w-[10%] px-3 py-4 text-sm flex gap-2" @click.stop>
                  <!-- 查看用户按钮 -->
                  <button
                    class="link-btn-view font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    @click="viewUser(user)" title="查看用户" :disabled="isImpersonating">
                    查看
                  </button>

                  <!-- 编辑用户按钮 -->
                  <button class="link-btn-edit font-medium" @click="editUser(user)"
                    title="编辑用户">
                    编辑
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination controls -->
    <Pagination v-if="totalUsers > 0"
      :total="totalUsers"
      v-model:current-page="currentPage"
      v-model:page-size="limit"
      :show-jumper="false"
      @update:current-page="fetchUsers"
      @update:page-size="fetchUsers"
    />

    <!-- Edit User Dialog -->
    <UserEditDialog v-model:visible="editDialogVisible" :user="selectedUser" :role-map="roleMap"
      @user-updated="handleUserUpdated" />

    <Modal
      :show="createUserModalVisible"
      @close="closeCreateUserModal"
      modal-size="sm:my-8 sm:w-full sm:max-w-lg sm:p-6"
    >
      <template #header>
        <span>创建用户</span>
      </template>
      <template #body>
        <form class="space-y-4" @submit.prevent="handleCreateUser">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              用户名 <span class="text-red-500">*</span>
            </label>
            <input
              v-model.trim="createUserForm.username"
              type="text"
              required
              minlength="3"
              maxlength="50"
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200"
              placeholder="例如: trial_user"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              邮箱
            </label>
            <input
              v-model.trim="createUserForm.email"
              type="email"
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200"
              placeholder="例如: user@example.com"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              手机号
            </label>
            <input
              v-model.trim="createUserForm.phone"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200"
              placeholder="没有手机号可以留空"
            />
            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
              邮箱和手机号至少填写一个。
            </p>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              初始密码 <span class="text-red-500">*</span>
            </label>
            <input
              v-model="createUserForm.password"
              type="password"
              required
              minlength="8"
              maxlength="128"
              autocomplete="new-password"
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200"
              placeholder="至少 8 位"
            />
          </div>
          <div class="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-700 dark:border-blue-900/60 dark:bg-blue-950/30 dark:text-blue-300">
            创建后会自动开通免费套餐、初始化配额、创建钱包和推荐码。
          </div>
        </form>
      </template>
      <template #footer>
        <button
          type="button"
          class="cancel-btn"
          @click="closeCreateUserModal"
        >
          取消
        </button>
        <button
          type="button"
          class="primary-btn ml-3 disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="creatingUser"
          @click="handleCreateUser"
        >
          {{ creatingUser ? '创建中...' : '创建用户' }}
        </button>
      </template>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import { createUserAPI, searchUsersAPI, impersonateUserAPI } from "@/api/user";
import { UserRole } from "@/types/auth/role";
import type { User, UserStatus } from "@/types/user/user";
import { useAuthStore } from "@/stores/auth";
import { debounce } from 'lodash-es';
import UserEditDialog from "@/components/membership/user-management/UserEditDialog.vue";
import Modal from "@/components/ui/Modal.vue";
import Pagination from '@/components/ui/Pagination.vue';
import { HttpStatus } from "@/types/axios/httpStatus";
import toast from "@/utils/toaster";

const authStore = useAuthStore();

// Pagination state
const users = ref<User[]>([]);
const currentPage = ref(1);
const limit = ref(10);
const totalUsers = ref(0);

// Search and filter state
const searchQuery = ref("");
const statusFilter = ref<UserStatus>("all");



const roleMap = ref<Record<UserRole, string>>({
  [UserRole.USER]: "普通用户",
  [UserRole.SUPER_ADMIN]: "超级管理员",
  [UserRole.ADMIN]: "管理员",
  [UserRole.TEACHER]: "教师",
});

// Edit dialog state
const editDialogVisible = ref(false);
const selectedUser = ref<User | null>(null);

// Impersonate state
const isImpersonating = ref(false);

const createUserModalVisible = ref(false);
const creatingUser = ref(false);
const createUserForm = ref({
  username: "",
  email: "",
  phone: "",
  password: "",
});

const resetCreateUserForm = () => {
  createUserForm.value = {
    username: "",
    email: "",
    phone: "",
    password: "",
  };
};

const openCreateUserModal = () => {
  resetCreateUserForm();
  createUserModalVisible.value = true;
};

const closeCreateUserModal = () => {
  if (creatingUser.value) return;
  createUserModalVisible.value = false;
};

const handleCreateUser = async () => {
  const username = createUserForm.value.username.trim();
  const email = createUserForm.value.email.trim();
  const phone = createUserForm.value.phone.trim();
  const password = createUserForm.value.password;

  if (!username) {
    toast.error("请输入用户名");
    return;
  }
  if (!email && !phone) {
    toast.error("邮箱和手机号至少填写一个");
    return;
  }
  if (password.length < 8) {
    toast.error("初始密码至少 8 位");
    return;
  }

  try {
    creatingUser.value = true;
    const response = await createUserAPI({
      username,
      email: email || null,
      phone: phone || null,
      password,
    });

    if (response.status !== HttpStatus.HTTP_200_OK) {
      return;
    }

    toast.success("用户创建成功");
    createUserModalVisible.value = false;
    resetCreateUserForm();
    currentPage.value = 1;
    await fetchUsers();
  } catch (error) {
    console.error("创建用户失败:", error);
    toast.error("创建用户失败，请稍后重试");
  } finally {
    creatingUser.value = false;
  }
};

// Helper functions
const getRoleBadgeClass = (role: UserRole): string => {
  const baseClasses = "px-2 py-1 text-xs font-medium rounded-full";
  switch (role) {
    case UserRole.SUPER_ADMIN:
      return `${baseClasses} bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400`;
    case UserRole.ADMIN:
      return `${baseClasses} bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400`;
    case UserRole.TEACHER:
      return `${baseClasses} bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400`;
    default:
      return `${baseClasses} bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300`;
  }
};



const formatDate = (date: string | null): string => {
  if (!date) return '未设置';
  return new Date(date).toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * 判断订阅是否有效
 */
const isSubscriptionActive = (subscription: { status: string; endDate: string | null }): boolean => {
  if (subscription.status !== 'ACTIVE') return false;
  if (!subscription.endDate) return true; // 永久订阅
  return new Date(subscription.endDate) > new Date();
};

/**
 * 格式化订阅状态
 */
const formatSubscriptionStatus = (status: string): string => {
  const statusMap: Record<string, string> = {
    'PENDING': '待激活',
    'ACTIVE': '有效',
    'EXPIRED': '已过期',
    'CANCELLED': '已取消',
  };
  return statusMap[status] || status;
};

/**
 * 获取订阅状态徽章样式
 */
const getSubscriptionStatusClass = (status: string): string => {
  const baseClasses = "inline-flex items-center rounded-md px-2 py-1 text-xs font-medium";
  switch (status) {
    case 'ACTIVE':
      return `${baseClasses} bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400`;
    case 'PENDING':
      return `${baseClasses} bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400`;
    case 'EXPIRED':
      return `${baseClasses} bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400`;
    case 'CANCELLED':
      return `${baseClasses} bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300`;
    default:
      return `${baseClasses} bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300`;
  }
};

// Fetch users with search and filters
const fetchUsers = async () => {
  try {
    const response = await searchUsersAPI({
      query: searchQuery.value,
      status: statusFilter.value,
      page: currentPage.value,
      limit: limit.value,
    });

    users.value = response.data.users ?? [];
    totalUsers.value = response.data.totalUsers ?? 0;
    currentPage.value = response.data.currentPage ?? 1;
  } catch (error) {
    console.error("Error fetching users:", error);
  }
};

// Debounced search handler
const handleSearch = debounce(() => {
  currentPage.value = 1; // Reset to first page when searching
  fetchUsers();
}, 300);

// Edit user handler
const editUser = (user: User) => {
  selectedUser.value = user;
  editDialogVisible.value = true;
};

/**
 * 查看用户（模拟登录）
 *
 * 以指定用户的身份打开 Home Frontend，允许超级管理员查看用户界面
 *
 * @param user - 要查看的用户对象
 */
const viewUser = async (user: User) => {
  // 防止重复点击
  if (isImpersonating.value) return;

  try {
    isImpersonating.value = true;

    // 1. 调用模拟登录 API
    const response = await impersonateUserAPI(user.id);

    // 2. 获取临时 Token
    const { accessToken } = response.data;

    // 3. 构建 Home Frontend Callback URL
    const homeBaseUrl = import.meta.env.VITE_MAIN_APP_FRONTEND_URL || 'http://localhost:3000';
    const callbackUrl = `${homeBaseUrl}/auth/impersonate-callback?token=${accessToken}`;

    // 4. 在新标签页打开
    window.open(callbackUrl, '_blank');

    // 5. 显示成功提示
    toast.success(`正在以 ${user.username} 的身份查看系统`);

  } catch (error: any) {
    console.error("模拟登录失败:", error);

    // 根据错误类型显示不同的提示
    if (error.response?.status === 403) {
      toast.error("权限不足，只有超级管理员才能使用此功能");
    } else if (error.response?.status === 404) {
      toast.error("用户不存在");
    } else {
      toast.error("模拟登录失败，请稍后重试");
    }
  } finally {
    isImpersonating.value = false;
  }
};

// Handle user updated
const handleUserUpdated = (updatedUser?: User) => {
  if (updatedUser) {
    // 局部更新：在列表中找到并更新该用户
    const index = users.value.findIndex(u => u.id === updatedUser.id);
    if (index !== -1) {
      users.value[index] = updatedUser;
      // 同时更新 selectedUser（如果对话框还开着）
      if (selectedUser.value && selectedUser.value.id === updatedUser.id) {
        selectedUser.value = updatedUser;
      }
    }
  } else {
    // 全量刷新：重新获取用户列表
    fetchUsers();
  }
};

// Fetch users on component mount
onMounted(() => {
  fetchUsers();
});
</script>

<style scoped>
.sticky {
  box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1);
}
</style>
